import React, { useState, useEffect, useCallback } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { 
  Container, 
  Segment, 
  Header, 
  Table, 
  Checkbox, 
  Button, 
  Dropdown, 
  Input,
  Icon,
  Message,
  Loader,
  Label,
  Modal,
  Grid,
  Card,
  Statistic
} from 'semantic-ui-react';

import GoBack from '../../../components/go-back';
import BigDataCorpConfigModal from './components/config-modal';
import UnitsManagementService from '../../../services/units-management.service';
import BigDataCorpSyncService from '../../../services/bigdatacorp-sync.service';

import './styles.css';
import './units-management.css';

const UnitsManagement = () => {
  const match = useRouteMatch();
  const { id: condoId } = match.params;
  
  const [loading, setLoading] = useState(true);
  const [unitsData, setUnitsData] = useState(null);
  const [filteredUnits, setFilteredUnits] = useState([]);
  const [filters, setFilters] = useState({
    block: '',
    unit: ''
  });
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState({
    active: false,
    lastSync: null,
    syncCount: 0
  });

  const normalizeUnitId = (unitId) => {
    if (typeof unitId === 'object') {
      return unitId._id || unitId.toString();
    }
    return unitId;
  };

  const fetchUnits = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await UnitsManagementService.getUnits(condoId);
      setUnitsData(data);
      setFilteredUnits(data.units);
    } catch (err) {
      setError('Erro ao carregar unidades. Verifique sua conexão.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [condoId]);

  

  const setupSyncService = useCallback(() => {
    const syncService = BigDataCorpSyncService.getInstance();
    
    const handleSyncUpdate = (data) => {
      if (data.hasChanges) {
        fetchUnits();
        setSuccess(`Dados atualizados automaticamente (${new Date().toLocaleTimeString()})`);
        setTimeout(() => setSuccess(null), 3000);
        
        setSyncStatus(prev => ({
          ...prev,
          lastSync: new Date(),
          syncCount: prev.syncCount + 1
        }));
      }
    };
    
    const handleSyncStatus = (isActive) => {
      setSyncStatus(prev => ({
        ...prev,
        active: isActive
      }));
    };
    syncService.addListener('syncUpdate', handleSyncUpdate);
    syncService.addListener('syncStatus', handleSyncStatus);
    syncService.startSync(condoId);

    window._adminSyncService = syncService;
    window._adminSyncUpdateListener = handleSyncUpdate;
    window._adminSyncStatusListener = handleSyncStatus;
    
  }, [condoId, fetchUnits]);

  const cleanupSyncService = useCallback(() => {
    if (window._adminSyncService) {
      if (window._adminSyncUpdateListener) {
        window._adminSyncService.removeListener('syncUpdate', window._adminSyncUpdateListener);
        delete window._adminSyncUpdateListener;
      }
      
      if (window._adminSyncStatusListener) {
        window._adminSyncService.removeListener('syncStatus', window._adminSyncStatusListener);
        delete window._adminSyncStatusListener;
      }

      window._adminSyncService.stopSync();
      delete window._adminSyncService;
    }
  }, []);

  useEffect(() => {
    fetchUnits();
    setupSyncService();

    return () => {
      cleanupSyncService();
    };
  }, [fetchUnits, condoId, setupSyncService, cleanupSyncService]);

  useEffect(() => {
    if (!unitsData) return;

    let filtered = unitsData.units;

    if (filters.block) {
      filtered = filtered.filter(unit => 
        unit.block && unit.block.toString().includes(filters.block)
      );
    }

    if (filters.unit) {
      filtered = filtered.filter(unit => 
        unit.identifier.toLowerCase().includes(filters.unit.toLowerCase())
      );
    }

    setFilteredUnits(filtered);
  }, [unitsData, filters]);

  const getBlockOptions = () => {
    if (!unitsData) return [];
    const blocks = [...new Set(unitsData.units.map(unit => unit.block).filter(Boolean))];
    return blocks.map(block => ({
      key: block,
      value: block,
      text: `Bloco ${block}`
    }));
  };

  const handleToggleBigDataCorp = (unit, enabled) => {
    if (enabled && !unit.bigDataCorp.canEnable) {
      setError('BigDataCorp não está habilitado para este condomínio');
      return;
    }

    if (enabled) {
      setPendingAction({ type: 'enable', unit, enabled });
      setConfirmModalOpen(true);
    } else {
      performToggleAction({ type: 'disable', unit, enabled });
    }
  };

  const performToggleAction = async (action) => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);

      const { unit, enabled } = action;
      const unitId = normalizeUnitId(unit.unitId);

      if (enabled) {
        await UnitsManagementService.enableBigDataCorp(unitId);
        setSuccess(`BigDataCorp habilitado para a unidade ${unit.identifier} com sucesso!`);
      } else {
        await UnitsManagementService.disableBigDataCorp(unitId);
        setSuccess(`BigDataCorp desabilitado para a unidade ${unit.identifier}`);
      }

      await fetchUnits();
    } catch (err) {
      setError(action.enabled 
        ? 'Erro ao habilitar BigDataCorp. Tente novamente.' 
        : 'Erro ao desabilitar BigDataCorp. Tente novamente.'
      );
      console.error(err);
    } finally {
      setActionLoading(false);
      setConfirmModalOpen(false);
      setPendingAction(null);
    }
  };

  const handleConfirmAction = () => {
    if (pendingAction) {
      performToggleAction(pendingAction);
    }
  };

  const handleConfigUnit = async (unit) => {
    try {
      setError(null);
      const unitId = normalizeUnitId(unit.unitId);
      const unitData = await UnitsManagementService.getUnitDetails(unitId);
      setSelectedUnit(unitData);
      setModalOpen(true);
    } catch (err) {
      setError('Erro ao carregar configurações da unidade');
      console.error(err);
    }
  };

  const handleSaveConfig = async (unitId, config) => {
    try {
      setError(null);
      await UnitsManagementService.updateUnitConfig(unitId, config);
      setSuccess('Configurações salvas com sucesso!');
      
      const updatedUnitData = await UnitsManagementService.getUnitDetails(unitId);
      
      setUnitsData(prevData => {
        if (!prevData || !prevData.units) return prevData;
        const updatedUnits = prevData.units.map(unit => 
          unit.unitId === unitId ? updatedUnitData : unit
        );
        return { ...prevData, units: updatedUnits };
      });
      
      setFilteredUnits(prevFiltered => {
        const updated = prevFiltered.map(unit => 
          unit.unitId === unitId ? updatedUnitData : unit
        );
        return updated;
      });
      
      const correctedUnitData = {
        ...updatedUnitData,
        _id: updatedUnitData.unitId,
        id: updatedUnitData.unitId
      };
      
      setSelectedUnit(correctedUnitData);
      
      setTimeout(() => {
        setModalOpen(false);
        setSelectedUnit(null);
      }, 1500);
    } catch (err) {
      setError('Erro ao salvar configurações. Tente novamente.');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <Container>
        <Segment placeholder className="loading-segment">
          <Loader active inline="centered" size="large">
            <div className="loading-text">
              Carregando dados das unidades...
            </div>
          </Loader>
        </Segment>
      </Container>
    );
  }

  const stats = {
    total: filteredUnits.length,
    enabled: filteredUnits.filter(unit => unit.bigDataCorp.enabled).length,
    disabled: filteredUnits.filter(unit => !unit.bigDataCorp.enabled).length
  };

  return (
    <Container fluid className="units-container">
      <div className="go-back-container">
        <GoBack route={`/buildings/${condoId}`} />
      </div>

      <div className="header-card">
        <div className="header-content">
          <div className="header-left">
            <div className="header-title-section">
              <div className="header-icon">
                <Icon name="building outline" />
              </div>
              <div className="header-text">
                <Header as="h1" className="header-title">
                  {unitsData?.condoName || 'Carregando...'}
                </Header>
                <p className="header-subtitle">
                  Gerenciamento BigDataCorp por Unidade
                </p>
              </div>
            </div>
          </div>
          <div className="status-indicators">
            <div className={`status-badge ${unitsData?.condoBigDataCorpEnabled ? 'active' : 'inactive'}`}>
              <div className={`status-dot ${unitsData?.condoBigDataCorpEnabled ? 'active' : 'inactive'}`} />
              <span className={`status-text ${unitsData?.condoBigDataCorpEnabled ? 'active' : 'inactive'}`}>
                BigDataCorp {unitsData?.condoBigDataCorpEnabled ? 'Ativo' : 'Inativo'}
              </span>
            </div>
            
            <div className={`sync-badge ${syncStatus.active ? 'active' : 'inactive'}`}>
              <Icon 
                name={syncStatus.active ? 'sync' : 'pause'} 
                className={`${syncStatus.active ? 'rotating' : ''} sync-icon ${syncStatus.active ? 'active' : 'inactive'}`}
              />
              <span className={`sync-text ${syncStatus.active ? 'active' : 'inactive'}`}>
                Sync {syncStatus.active ? 'Ativo' : 'Inativo'}
              </span>
              {syncStatus.lastSync && (
                <span className="sync-time">
                  ({syncStatus.lastSync.toLocaleTimeString()})
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <Grid columns={4} stackable className="stats-grid">
        <Grid.Column>
          <Card fluid className="stat-card">
            <Card.Content textAlign="center" className="stat-content">
              <Statistic color="blue">
                <Statistic.Value>{stats.total}</Statistic.Value>
                <Statistic.Label className="stat-label">
                  <Icon name="home" />
                  Total de Unidades
                </Statistic.Label>
              </Statistic>
            </Card.Content>
          </Card>
        </Grid.Column>
        
        <Grid.Column>
          <Card fluid className="stat-card">
            <Card.Content textAlign="center" className="stat-content">
              <Statistic color="green">
                <Statistic.Value>{stats.enabled}</Statistic.Value>
                <Statistic.Label className="stat-label">
                  <Icon name="check circle" />
                  BigDataCorp Ativo
                </Statistic.Label>
              </Statistic>
            </Card.Content>
          </Card>
        </Grid.Column>
        
        <Grid.Column>
          <Card fluid className="stat-card">
            <Card.Content textAlign="center" className="stat-content">
              <Statistic color="grey">
                <Statistic.Value>{stats.disabled}</Statistic.Value>
                <Statistic.Label className="stat-label">
                  <Icon name="times circle" />
                  BigDataCorp Inativo
                </Statistic.Label>
              </Statistic>
            </Card.Content>
          </Card>
        </Grid.Column>
        
        <Grid.Column>
          <Card fluid className="stat-card">
            <Card.Content textAlign="center" className="stat-content">
              <Statistic color="orange">
                <Statistic.Value>{Math.round((stats.enabled / stats.total) * 100) || 0}%</Statistic.Value>
                <Statistic.Label className="stat-label">
                  <Icon name="percent" />
                  Taxa de Adoção
                </Statistic.Label>
              </Statistic>
            </Card.Content>
          </Card>
        </Grid.Column>
      </Grid>

      {/* Mensagens */}
      {error && (
        <Message negative onDismiss={() => setError(null)} className="message">
          <Message.Header>
            <Icon name="exclamation triangle" />
            Erro
          </Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {success && (
        <Message positive onDismiss={() => setSuccess(null)} className="message">
          <Message.Header>
            <Icon name="check circle" />
            Sucesso
          </Message.Header>
          <p>{success}</p>
        </Message>
      )}

      <div className="filters-card">
        <div className="filters-header">
          <h4 className="filters-title">
            Filtros
          </h4>
          <p className="filters-subtitle">
            Filtre as unidades por bloco e número
          </p>
        </div>
        
        <Grid columns={3} stackable>
          <Grid.Column>
            <label className="filter-label">
              <Icon name="building" size="small" />
              Bloco
            </label>
            <Dropdown
              placeholder="Selecione um bloco"
              fluid
              selection
              clearable
              value={filters.block}
              options={getBlockOptions()}
              onChange={(e, { value }) => setFilters(prev => ({ ...prev, block: value || '' }))}
              className="filter-dropdown"
            />
          </Grid.Column>
          
          <Grid.Column>
            <label className="filter-label">
              <Icon name="home" size="small" />
              Número da Unidade
            </label>
            <Input
              placeholder="Ex: 101, 201..."
              fluid
              value={filters.unit}
              onChange={(e, { value }) => setFilters(prev => ({ ...prev, unit: value }))}
              className="filter-input"
              icon={<Icon name="search" />}
            />
          </Grid.Column>
          
          <Grid.Column>
            <div className="clear-filters-section">
              <Button
                basic
                icon
                labelPosition="left"
                onClick={() => setFilters({ block: '', unit: '' })}
                className="clear-filters-btn"
              >
                <Icon name="refresh" />
                Limpar Filtros
              </Button>
            </div>
          </Grid.Column>
        </Grid>
      </div>

      <div className="table-container">
        <Table celled selectable className="units-table">
          <Table.Header>
            <Table.Row className="table-header">
              <Table.HeaderCell className="table-header-cell">
                <Icon name="building" />
                Bloco
              </Table.HeaderCell>
              <Table.HeaderCell className="table-header-cell">
                <Icon name="home" />
                Unidade
              </Table.HeaderCell>
              <Table.HeaderCell className="table-header-cell">
                <Icon name="tag" />
                Tipo
              </Table.HeaderCell>
              <Table.HeaderCell className="table-header-cell">
                <Icon name="users" />
                Moradores
              </Table.HeaderCell>
              <Table.HeaderCell className="table-header-cell">
                <Icon name="user" />
                Responsável
              </Table.HeaderCell>
              <Table.HeaderCell textAlign="center" className="table-header-cell">
                <Icon name="shield" />
                BigDataCorp
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {filteredUnits.map(unit => (
              <Table.Row key={normalizeUnitId(unit.unitId)} className="table-row">
                <Table.Cell className="table-cell bold">
                  <Label size="small" className="block-label">
                    {unit.block || '01'}
                  </Label>
                </Table.Cell>
                <Table.Cell className="table-cell">
                  <div className="unit-info">
                    <strong className="unit-identifier">{unit.identifier}</strong>
                    {unit.label && (
                      <div className="unit-label">
                        {unit.label}
                      </div>
                    )}
                  </div>
                </Table.Cell>
                <Table.Cell className="table-cell">
                  <Label size="small" className="type-label">
                    Comum
                  </Label>
                </Table.Cell>
                <Table.Cell className="table-cell">
                  <div className="residents-info">
                    <Icon name="users" size="small" />
                    2
                  </div>
                </Table.Cell>
                <Table.Cell className="table-cell">
                  <div className="owner-info">
                    <span className="owner-name">
                      {unit.owner || 'Não informado'}
                    </span>
                    {unit.isRented && (
                      <Label size="tiny" className="rental-label">
                        <Icon name="key" size="small" />
                        Alugado
                      </Label>
                    )}
                  </div>
                </Table.Cell>
                <Table.Cell textAlign="center" className="table-cell">
                  <div className="bigdatacorp-actions">
                    <Checkbox
                      toggle
                      checked={unit.bigDataCorp.enabled}
                      onChange={(e, { checked }) => handleToggleBigDataCorp(unit, checked)}
                      disabled={actionLoading}
                    />
                    {unit.bigDataCorp.enabled && (
                      <Button
                        size="small"
                        basic
                        compact
                        onClick={() => handleConfigUnit(unit)}
                        title="Configurar alertas"
                        className="config-btn"
                      >
                        <Icon name="cog" />
                        Configurar
                      </Button>
                    )}
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>

        {filteredUnits.length === 0 && (
          <div className="empty-state">
            <Icon name="search" size="big" color="grey" />
            <Header as="h4" color="grey" className="empty-header">
              Nenhuma unidade encontrada
            </Header>
            <p className="empty-text">
              Não há unidades que correspondam aos filtros aplicados.
            </p>
          </div>
        )}
      </div>
      <Modal
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        size="small"
        closeIcon
        className="confirm-modal"
      >
        <div className="modal-header">
          <div className="modal-header-icon">
            <Icon name="shield" />
          </div>
          <div>
            <Header as="h3" className="modal-title">
              Confirmar Habilitação do BigDataCorp
            </Header>
            <p className="modal-subtitle">
              Ativação de monitoramento de segurança
            </p>
          </div>
        </div>

        <Modal.Content className="modal-content">
          <div className="modal-content-center">
            <div className="question-icon-container">
              <div className="question-decoration" />
              <Icon name="question circle" className="question-icon" />
            </div>

            <Header as="h2" className="modal-question-title">
              Deseja habilitar o BigDataCorp para esta unidade?
            </Header>

            {pendingAction && (
              <div className="unit-selection-card">
                <div className="unit-selection-header"></div>
                
                <div className="unit-selection-content">
                  <div className="unit-selection-icon">
                    <Icon name="home" />
                  </div>
                  <div className="unit-selection-info">
                    <div className="unit-selection-title">
                      Unidade {pendingAction.unit?.identifier}
                    </div>
                    <div className="unit-selection-block">
                      Bloco {pendingAction.unit?.block}
                    </div>
                  </div>
                  <div className="unit-selection-badge">
                    <span className="unit-selection-badge-text">
                      Selecionada
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="info-section">
              <div className="info-decoration"></div>
              
              <div className="info-header">
                <div className="info-icon">
                  <Icon name="info circle" />
                </div>
                <h4 className="info-title">
                  O que acontece ao habilitar?
                </h4>
              </div>
              
              <div className="info-grid">
                <div className="info-item">
                  <div className="info-item-icon">
                    <Icon name="shield" />
                  </div>
                  <span className="info-item-text">Consultas automáticas de antecedentes criminais</span>
                </div>
                <div className="info-item">
                  <div className="info-item-icon">
                    <Icon name="mail" />
                  </div>
                  <span className="info-item-text">Notificações por email e WhatsApp configuráveis</span>
                </div>
              </div>
            </div>
          </div>
        </Modal.Content>

        <div className="modal-footer">
          <div className="modal-footer-info">
            <div className="modal-footer-lock">
              <Icon name="lock" />
            </div>
            Configurações seguras e privadas
          </div>
          
          <div className="modal-footer-actions">
            <Button 
              onClick={() => setConfirmModalOpen(false)} 
              size="medium"
              className="modal-cancel-btn"
            >
              <Icon name="times" style={{ marginRight: '8px' }} />
              Cancelar
            </Button>
            
            <Button 
              primary
              onClick={handleConfirmAction}
              loading={actionLoading}
              size="medium"
              className="modal-confirm-btn"
            >
              <Icon name="shield" style={{ marginRight: '8px' }} />
              Confirmar Habilitação
            </Button>
          </div>
        </div>
      </Modal>

      <BigDataCorpConfigModal
        open={modalOpen}
        unit={selectedUnit}
        onClose={() => {
          setModalOpen(false);
          setSelectedUnit(null);
        }}
        onSave={handleSaveConfig}
      />
    </Container>
  );
};

export default UnitsManagement;